import { Container } from "../../HtmlTags/Container/Container";
import { Span } from "../../HtmlTags/Span/Span";
import "./FormFieldRow.css";

export type FormFieldRowProps = {
  required?: boolean;
  wrapperClass?: string;
  label?: string | undefined;
  labelFor?: string | undefined;
  errorMessage?: string;
  children: React.ReactNode;
};

export default function FormFieldRow({
  required = false,
  wrapperClass = "",
  label,
  labelFor = "",
  children,
  errorMessage,
}: FormFieldRowProps) {
  return (
    <Container className={`form-field-row ${wrapperClass}`} data-testid="form-field-row">
      {label && (
        <label htmlFor={labelFor} data-testid="form-field-row-label">
          {label}
          {required && <Span className="required-asterisk">*</Span>}
        </label>
      )}
      {children}
      {errorMessage && (
        <Span
          className="form-field-row-error-message"
          data-testid="form-field-row-error"
        >
          {errorMessage}
        </Span>
      )}
    </Container>
  );
}

/**
 * FormFieldRow - A reusable form field wrapper component that provides consistent layout
 * for form inputs with optional labels and error messages.
 *
 * This component creates a standardized form field structure that includes:
 * - An optional label that can be associated with form controls
 * - The main form control (passed as children)
 * - An optional error message displayed below the field
 *
 * @component
 * @example
 * ```tsx
 * <FormFieldRow
 *   label="Email Address"
 *   labelFor="email"
 *   errorMessage="Please enter a valid email address"
 * >
 *   <InputField
 *     id="email"
 *     type="email"
 *     placeholder="Enter your email"
 *   />
 * </FormFieldRow>
 * ```
 *
 * @example
 * ```tsx
 * <FormFieldRow
 *   wrapperClass="custom-field-row"
 *   label="Password"
 *   labelFor="password"
 * >
 *   <InputField
 *     id="password"
 *     type="password"
 *     placeholder="Enter your password"
 *   />
 * </FormFieldRow>
 * ```
 *
 * @param {FormFieldRowProps} props - The component props
 * @param {string} [props.wrapperClass=""] - Additional CSS classes to apply to the wrapper container
 * @param {string} [props.label] - The label text to display above the form field
 * @param {string} [props.labelFor=""] - The ID of the form control to associate with the label (for accessibility)
 * @param {string} [props.errorMessage] - Error message to display below the form field
 * @param {React.ReactNode} props.children - The form control element(s) to render
 *
 * @returns {JSX.Element} A form field row with optional label and error message
 */
